import { TourFormDeparture } from '~/services/ToursService';

import { SlidingScaleByDeparturePricingItem } from '~/types/tours/costPrice';

/**
 * What both by-departure shapes have in common. A sliding scale carries a rate
 * per pax count and a fixed price a single rate, but each is priced against a
 * departure within a season, which is all the grouping here needs.
 */
export interface DepartureItem {
  departure: string;
  season: string;
}

/** One departure of a service, with everything priced against it. */
export interface DepartureRow<T extends DepartureItem = SlidingScaleByDeparturePricingItem> {
  key: string;
  /** Active package departure used to fetch inventory. */
  packageDepartureId?: string;
  /**
   * The departure's id, which is how Admin keys prices. Names are not unique -
   * a tour can run several departures under one - so keying by name made those
   * departures share a price with no way to tell them apart.
   */
  departure: string;
  /** The departure's name, e.g. SD-0020516, for reading and for sheets. */
  departureName: string;
  seasons: Array<string>;
  /** The date it leaves, absent where the tour no longer runs it. */
  departureDate?: string;
  /** False where something is priced against a departure the tour does not run. */
  isKnownDeparture: boolean;
  /** Everything priced against this departure, for the table that shows it. */
  items: Array<T>;
}

/** One selector entry shared by tour configurations leaving on the same date and season. */
export function departureSelectorKey(departure: { departureName: string | null; departureDate?: string }): string {
  return `${departure.departureDate ?? ''}::${departure.departureName ?? ''}`;
}

export function groupDeparturesForSelector<T extends { departureName: string | null; departureDate?: string }>(
  departures: Array<T>,
): Array<[string, Array<T>]> {
  const groups = new Map<string, Array<T>>();

  departures.forEach((departure) => {
    const key = departureSelectorKey(departure);
    groups.set(key, (groups.get(key) ?? []).concat(departure));
  });

  return Array.from(groups.entries());
}

/**
 * Groups a service's cost prices by the departure they are keyed against.
 *
 * svc-tour keys a by-departure price with the departure's name rather than its
 * id, and exposes that name on no endpoint Admin can call, so the departures
 * listed here are the ones already priced. Until a departure list carrying
 * names exists, Admin cannot know which of a tour's departures have none.
 */
export function buildDepartureRows<T extends DepartureItem>(
  departures: Array<TourFormDeparture>,
  items: Array<T>,
): Array<DepartureRow<T>> {
  const itemsByDeparture = new Map<string, Array<T>>();

  items.forEach((item) => {
    itemsByDeparture.set(item.departure, (itemsByDeparture.get(item.departure) ?? []).concat(item));
  });

  // Chronological: a departure is looked for by when it leaves, and the order
  // svc-tour returns them in is the tour's option and season nesting.
  const named = departures
    .filter((departure) => !!departure.departureName)
    .slice()
    .sort((a, b) => a.departureDate.localeCompare(b.departureDate));

  const rows: Array<DepartureRow<T>> = named.map((departure) => {
    const departureItems = itemsByDeparture.get(departure.departureId) ?? [];

    return {
      key: departure.departureId,
      packageDepartureId: departure.packageDepartureId,
      departure: departure.departureId,
      departureName: departure.departureName,
      departureDate: departure.departureDate,
      isKnownDeparture: true,
      seasons: Array.from(new Set(departureItems.map((item) => item.season))).sort(),
      items: departureItems,
    };
  });

  const knownIds = new Set(named.map((departure) => departure.departureId));

  // Something priced against a departure the tour does not run will never
  // resolve, so it is listed rather than quietly dropped.
  const unknown = Array.from(itemsByDeparture.entries())
    .filter(([departure]) => !knownIds.has(departure))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([departure, departureItems]) => ({
      key: departure,
      departure,
      departureName: departure,
      isKnownDeparture: false,
      seasons: Array.from(new Set(departureItems.map((item) => item.season))).sort(),
      items: departureItems,
    }));

  return rows.concat(unknown);
}

export function hasPrice(row: DepartureRow<DepartureItem>): boolean {
  return row.items.length > 0;
}

/**
 * Identifies one priced cell: a departure's season, package option and
 * currency. Re-uploading the same cell replaces it; anything else is left be.
 */
function itemKey(item: SlidingScaleByDeparturePricingItem): string {
  return [item.departure, item.season, item.tourConfiguration, item.packageConfiguration, item.currency].join('::');
}

/**
 * Folds an uploaded sheet into what the service already holds.
 *
 * An upload replaces a package option's stored pricing outright rather than
 * merging into it, so sending only the sheet someone just filled in would drop
 * every departure it does not mention. Admin sends the union instead, which is
 * what lets a sheet describe one departure without touching the rest.
 */
export function mergePricingItems(
  existing: Array<SlidingScaleByDeparturePricingItem>,
  uploaded: Array<SlidingScaleByDeparturePricingItem>,
): Array<SlidingScaleByDeparturePricingItem> {
  const uploadedKeys = new Set(uploaded.map(itemKey));

  return existing.filter((item) => !uploadedKeys.has(itemKey(item))).concat(uploaded);
}

/** What an upload will do to the departures already priced, for confirming. */
export function summariseMerge(
  existing: Array<SlidingScaleByDeparturePricingItem>,
  uploaded: Array<SlidingScaleByDeparturePricingItem>,
): { added: Array<string>; replaced: Array<string>; untouched: Array<string> } {
  const existingDepartures = new Set(existing.map((item) => item.departure));
  const uploadedDepartures = Array.from(new Set(uploaded.map((item) => item.departure))).sort();

  return {
    added: uploadedDepartures.filter((departure) => !existingDepartures.has(departure)),
    replaced: uploadedDepartures.filter((departure) => existingDepartures.has(departure)),
    untouched: Array.from(existingDepartures)
      .filter((departure) => !uploadedDepartures.includes(departure))
      .sort(),
  };
}

/**
 * Finds the departure a sheet's Departure cell names.
 *
 * Sheets carry names because nobody types a uuid, but names are not unique, so
 * one shared by several departures is rejected rather than guessed at - the
 * departure picker is how those are priced.
 */
export function resolveDepartureId(
  departures: Array<TourFormDeparture>,
  value: string,
): { id?: string; error?: string } {
  const byId = departures.find((departure) => departure.departureId === value);
  if (byId) {
    return { id: byId.departureId };
  }

  const matches = departures.filter((departure) => departure.departureName === value);

  if (!matches.length) {
    return { error: `${value} is not a departure on this tour` };
  }

  if (matches.length > 1) {
    return {
      error: `${value} is the name of ${matches.length} departures on this tour, so choose the departure instead of naming it`,
    };
  }

  return { id: matches[0].departureId };
}

/**
 * Resolves the departure every uploaded row names, so a sheet can be sent
 * keyed by id.
 *
 * A row whose departure cannot be resolved is dropped and reported rather than
 * guessed at, since guessing would price the wrong departure.
 */
export function resolveUploadedDepartures(
  departures: Array<TourFormDeparture>,
  rows: Array<{ item: SlidingScaleByDeparturePricingItem; rowNumber: number }>,
): { items: Array<SlidingScaleByDeparturePricingItem>; errors: Array<string> } {
  const items: Array<SlidingScaleByDeparturePricingItem> = [];
  const errors: Array<string> = [];

  rows.forEach(({ item, rowNumber }) => {
    const { id, error } = resolveDepartureId(departures, item.departure);

    if (error) {
      errors.push(`Row ${rowNumber}: ${error}`);
      return;
    }

    items.push({ ...item, departure: id });
  });

  return { items, errors };
}
